// stores/panelDataStore.ts — per-symbol candle/trade-flow data layer (design §4).
//
// Candles are keyed by SYMBOL ONLY (one 1m base series per symbol, shared across
// every timeframe via aggregateCandles at render). Two-layer refcount (§4.2):
// Layer-1 per-symbol refcount owns the raw gateway.subscribe/unsubscribe;
// interval is client-side routing only. Per-symbol WS gate buffers live bars
// until the 1m REST seed lands (§4.3); ensureLoaded coalesces concurrent loads
// and drives the candle-gate state machine (§4.4). Server signal series remain
// keyed by symbol:interval in signalByKey.
//
// LIVE PATH (Phase 3b cutover): live ohlc/signals reach this store via
// api/dispatchers/panelChartDispatcher.ts → dispatchPanelChartMessage, the SOLE
// chart-data consumer in messageDispatcher (the legacy global chartStore path was
// retired). REST still seeds the base 1m series (ensureLoaded → loadSymbol1m);
// appendCandle merges live 1m bars, seedFromSnapshot only from idle/awaiting-seed.

import { create } from 'zustand';
import type { PanelDataEntry, PanelDataState } from './panelDataStore.types';
import {
  BUFFER_MAX,
  clearSignalsForSymbol,
  dedupeSorted,
  drainBuffer,
  mergeCandle,
  omitKey,
} from './panelDataMutations';
import {
  closeSymbolSubscription,
  openSymbolSubscription,
} from '../api/dispatchers/panelSubscription';
import { inflightOlder, runLoadOlder } from './panelDataStore.older';
import { inflightDeepen, maybeDeepen } from './panelDataStore.deepen';
import { runEnsureLoaded } from './panelDataStore.load';
import { runAddEma, runAddRsi } from './panelDataStore.signals';
import {
  cancelPendingTeardown,
  inflight,
  pendingTeardown,
  TEARDOWN_GRACE_MS,
} from './panelDataStore.lifecycle';

export const usePanelDataStore = create<PanelDataState>((set, get) => ({
  dataByKey: {},
  signalByKey: {},
  refcounts: {},
  _buffers: {},
  _loading: {},
  _loadingOlder: {},
  _noMoreHistory: {},
  _olderAttempts: {},
  _seedDepth: {},
  _deepening: {},
  _pendingDeepen: {},

  // ── Layer-1 lifecycle (§4.2) ──────────────────────────────────────────────
  subscribe: (symbol, interval = 1) => {
    // A re-subscribe within the grace window cancels a pending teardown so the live
    // entry + in-flight REST seed survive a StrictMode unmount→remount (the empty-chart bug).
    cancelPendingTeardown(symbol);
    const next = (get().refcounts[symbol] ?? 0) + 1;
    set((s) => ({ refcounts: { ...s.refcounts, [symbol]: next } }));
    if (next === 1) {
      openSymbolSubscription(symbol); // ONE raw sub per symbol (idempotent)
      get().ensureLoaded(symbol, interval); // coalesced 1m REST seed; TF scales depth (F1, §4.4)
    } else {
      // A LATER subscriber on an already-loaded symbol: if its TF needs a deeper
      // 1m base than the first subscriber's load fetched (e.g. a 1m chart seeded,
      // now a 4h panel mounts), trigger a one-shot deeper re-fetch (Task 2). No-op
      // when the loaded depth already covers it or the symbol isn't 'ready' yet.
      maybeDeepen(get, set, symbol, interval);
    }
    // Idempotent unsub closure: guard against double-decrement under StrictMode.
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const left = (get().refcounts[symbol] ?? 1) - 1;
      if (left <= 0) {
        // Defer teardown: if the panel remounts (StrictMode) or the symbol is re-selected
        // within the grace window, subscribe() cancels this and the feed is never dropped.
        set((s) => ({ refcounts: omitKey(s.refcounts, symbol) }));
        cancelPendingTeardown(symbol);
        const timer = setTimeout(() => {
          pendingTeardown.delete(symbol);
          if ((get().refcounts[symbol] ?? 0) > 0) return; // re-subscribed → keep alive
          closeSymbolSubscription(symbol); // ONE raw unsub (idempotent)
          get().clear(symbol);
        }, TEARDOWN_GRACE_MS);
        pendingTeardown.set(symbol, timer);
      } else {
        set((s) => ({ refcounts: { ...s.refcounts, [symbol]: left } }));
      }
    };
  },

  isSubscribed: (symbol) => (get().refcounts[symbol] ?? 0) > 0,

  // ── Fetch coalescing + candle-gate state machine (§4.4) ───────────────────
  // Thin delegate; the cache fast-path (F1b) + seed orchestration + re-deepen
  // hand-off (Task 2) live in panelDataStore.load.ts to keep this file < 250 LOC.
  ensureLoaded: (symbol, interval = 1) =>
    runEnsureLoaded(get, set, symbol, interval),

  // ── WS write paths (§4.5) — fed live by panelChartDispatcher (Phase 2) ────
  appendCandle: (symbol, candle) =>
    set((s) => {
      if ((s.refcounts[symbol] ?? 0) === 0) return s; // unwatched → drop
      const entry = s.dataByKey[symbol];
      if (!entry || entry.loadState !== 'ready') {
        // gate → buffer (bounded). awaiting-seed promotes on first live bar (NEW-4).
        const buf = [...(s._buffers[symbol] ?? []), candle].slice(-BUFFER_MAX);
        if (entry?.loadState === 'awaiting-seed') {
          const seeded: PanelDataEntry = {
            ...entry,
            candles: dedupeSorted(buf),
            loadState: 'ready',
            lastWsCandleAt: candle.startTs,
          };
          return {
            dataByKey: { ...s.dataByKey, [symbol]: seeded },
            _buffers: omitKey(s._buffers, symbol),
          };
        }
        return { _buffers: { ...s._buffers, [symbol]: buf } };
      }
      return {
        dataByKey: { ...s.dataByKey, [symbol]: mergeCandle(entry, candle) },
      };
    }),

  setCandles: (symbol, candles, flow) => {
    // Flip → 'ready' with the REST series (REST wins, NEW-3), then drain buffer.
    set((s) => {
      const prior = s.dataByKey[symbol];
      const entry: PanelDataEntry = {
        symbol,
        candles: dedupeSorted(candles),
        tradeFlow: flow,
        loadState: 'ready',
        lastWsCandleAt: prior?.lastWsCandleAt ?? 0,
      };
      return {
        dataByKey: { ...s.dataByKey, [symbol]: entry },
        _buffers: omitKey(s._buffers, symbol),
      };
    });
    drainBuffer(get, symbol, get()._buffers[symbol]);
  },

  setTradeFlow: (symbol, flow) =>
    set((s) => {
      const entry = s.dataByKey[symbol];
      // Only attach to a live entry, and don't clobber a flow the live WS stream may have already
      // started accumulating (appendTradeFlow). The REST flow seed is the historical backfill;
      // if the entry already has flow points, keep them (WS is fresher).
      if (!entry || entry.tradeFlow.length > 0 || flow.length === 0) return s;
      return {
        dataByKey: { ...s.dataByKey, [symbol]: { ...entry, tradeFlow: flow } },
      };
    }),

  seedFromSnapshot: (symbol, candles) => {
    // Only invoked from the idle/awaiting-seed guard in the dispatcher (§4.5),
    // so it can never clobber 'ready' (REST) or 'loading' (in-flight) data.
    set((s) => {
      const prior = s.dataByKey[symbol];
      const entry: PanelDataEntry = {
        symbol,
        candles: dedupeSorted(candles),
        tradeFlow: prior?.tradeFlow ?? [],
        loadState: 'ready',
        lastWsCandleAt: prior?.lastWsCandleAt ?? 0,
      };
      return {
        dataByKey: { ...s.dataByKey, [symbol]: entry },
        _buffers: omitKey(s._buffers, symbol),
      };
    });
    drainBuffer(get, symbol, get()._buffers[symbol]);
  },

  prependCandles: (symbol, older) =>
    set((s) => {
      const entry = s.dataByKey[symbol];
      if (!entry) return s;
      const merged = dedupeSorted([...older, ...entry.candles]);
      return {
        dataByKey: { ...s.dataByKey, [symbol]: { ...entry, candles: merged } },
      };
    }),

  // Left-edge infinite-scroll backfill (BUILD #1) — thin delegate; the single-flight +
  // window-widen + exhaustion-latch state machine lives in panelDataStore.older.ts.
  loadOlder: (symbol, interval) => runLoadOlder(get, set, symbol, interval),

  hasMoreHistory: (symbol) => !get()._noMoreHistory[symbol],

  appendTradeFlow: (symbol, point) =>
    set((s) => {
      const entry = s.dataByKey[symbol];
      if (!entry || (s.refcounts[symbol] ?? 0) === 0) return s;
      return {
        dataByKey: {
          ...s.dataByKey,
          [symbol]: { ...entry, tradeFlow: [...entry.tradeFlow, point] },
        },
      };
    }),

  // ── Server signal write paths (symbol:interval) — thin delegates (signals.ts) ─
  addEma: (key, period, point) => runAddEma(set, key, period, point),
  addRsi: (key, point) => runAddRsi(set, key, point),

  // ── Teardown (§4.2, refcount 1→0) ─────────────────────────────────────────
  clear: (symbol) => {
    inflight.get(symbol)?.abort(); // cancel any in-flight REST load
    inflight.delete(symbol);
    inflightOlder.get(symbol)?.abort(); // cancel any in-flight older-history load
    inflightOlder.delete(symbol);
    inflightDeepen.get(symbol)?.abort(); // cancel any in-flight deeper re-fetch
    inflightDeepen.delete(symbol);
    // NOTE: candleCache is NOT cleared here — surviving a refcount 1→0→1 cycle is
    // the whole point of the within-session cache (F1b).
    set((s) => ({
      dataByKey: omitKey(s.dataByKey, symbol),
      _buffers: omitKey(s._buffers, symbol),
      _loading: omitKey(s._loading, symbol),
      _loadingOlder: omitKey(s._loadingOlder, symbol),
      _noMoreHistory: omitKey(s._noMoreHistory, symbol),
      _olderAttempts: omitKey(s._olderAttempts, symbol),
      _seedDepth: omitKey(s._seedDepth, symbol),
      _deepening: omitKey(s._deepening, symbol),
      _pendingDeepen: omitKey(s._pendingDeepen, symbol),
      signalByKey: clearSignalsForSymbol(s.signalByKey, symbol),
    }));
  },
}));
